<template>
  <div class="saved-card">
    <div class="saved-card__brand">
      <i :class="brandIcon" />
    </div>
    <div class="saved-card__info">
      <div class="saved-card__number">
        <span>••••</span>
        <span>••••</span>
        <span>••••</span>
        <strong>{{ card?.last4 }}</strong>
      </div>
      <div class="saved-card__meta">
        {{ card?.brand }} · {{ t('billing.subscriptionPage.paymentMethods.expires') }} {{ card?.expiry }}
      </div>
    </div>
    <span
      v-if="showDefaultPill && card?.isDefault"
      class="saved-card__pill"
    >
      {{ t('billing.subscriptionPage.paymentMethods.default') }}
    </span>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import { brandIconFor } from '@/composables/billing/useBrandIcon';
import { PaymentMethodItem } from '@/types/billing-types';

export default defineComponent({
  name: 'SavedCardDisplay',

  props: {
    card: {
      type: Object as PropType<PaymentMethodItem | null>,
      default: null,
    },

    /** Whether to render the "default" pill in the corner. */
    showDefaultPill: {
      type: Boolean,
      default: true,
    },
  },

  setup(props) {
    const { t } = useI18n();
    const brandIcon = computed(() => brandIconFor(props.card?.brand));

    return { t, brandIcon };
  },
});
</script>

<style lang="scss" scoped>
.saved-card {
  position: relative;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: #fff;
  border-radius: 14px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 14px;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: -50px;
    right: -50px;
    width: 180px;
    height: 180px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(148, 200, 64, 0.22) 0%, transparent 70%);
    pointer-events: none;
  }

  &__brand {
    width: 46px;
    height: 32px;
    background: #fff;
    border-radius: 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    color: #1a1f36;
    flex-shrink: 0;
    z-index: 1;
  }

  &__info {
    flex: 1;
    z-index: 1;
    min-width: 0;
  }

  &__number {
    display: flex;
    gap: 8px;
    align-items: baseline;
    font-family: "SF Mono", "Roboto Mono", Menlo, monospace;
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.9375rem;
    letter-spacing: 0.08em;

    strong {
      color: #fff;
      font-weight: 600;
      letter-spacing: 0.04em;
    }
  }

  &__meta {
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.55);
    margin-top: 4px;
  }

  &__pill {
    z-index: 1;
    background: rgba(148, 200, 64, 0.22);
    color: #c1e07e;
    font-size: 0.625rem;
    text-transform: uppercase;
    font-weight: 700;
    letter-spacing: 0.06em;
    padding: 3px 8px;
    border-radius: 100px;
    flex-shrink: 0;
  }
}
</style>
